<template>
    <div class="module-calendar module-calendar--dates" v-on:mouseup="doneSelecting" v-on:mouseleave="doneSelecting">
        <div class="pager">
            <div>
                <action icon="prev" @click.native="prevMonth" color="transparent" />
            </div>
            <span @click="gotoToday">{{ headline }}</span>
            <div>
                <action icon="next" @click.native="nextMonth" color="transparent" />
            </div>
        </div>
        <table>
            <tr>
                <th>{{ $t('layout.calendar.mon') }}</th>
                <th>{{ $t('layout.calendar.tue') }}</th>
                <th>{{ $t('layout.calendar.wed') }}</th>
                <th>{{ $t('layout.calendar.thu') }}</th>
                <th>{{ $t('layout.calendar.fri') }}</th>
                <th>{{ $t('layout.calendar.sat') }}</th>
                <th>{{ $t('layout.calendar.sun') }}</th>
            </tr>
            <tr v-for="(days, week) in calendar">
                <td v-for="day in sortDays(days)"
                    :class="getClasses(day)"
                    @mousedown.stop="select($event, day)"
                    @mouseenter="dragSelect(day)"
                ><span>{{ day.day }}</span></td>
            </tr>
        </table>
    </div>
</template>

<script>
import events from '../../events/events';

export default
{
    name: 'Calendar',

    props:
    {
        /*
         * The callback property handles the selected dates
         * array and provides it in the callback function:
         * callbackName(selectedDates)
         */
        callback:
        {
            required: false,
            type: Function,
        },

        /*
         * Set current year and month of calendar
         * in order to prevent reset at init()
         */
        date:
        {
            required: false,
            type: String,
        },

        /*
         * Selection mode for calendar.
         * Multi allows multiple timeframes
         * Range allows defined ranges from day, to years
         * Single allows only on timeframe
         * Readonly allows only display of selected dates
         */
        mode:
        {
            required: false,
            type: String,
            default: 'multi'
        },

        range:
        {
            required: this.mode === 'range',
            type: String
        },

        /*
         * Blocks certain dates in the selection
         * @array ['2018-05-12']
         */
        blocked:
        {
            required: false,
            type: Array,
            default: () => []
        },

        /*
         * Pre-selects certain dates in the calendar
         * @array ['2018-05-12']
         */
        selected:
        {
            required: false,
            type: Array,
            default: () => []
        },

        /*
         * Circle selected dates
         */
        circled:
        {
            required: false,
            type: Boolean,
            default: false
        },

        /*
         * Higlight certain dates in the calendar
         * @array ['2018-05-12']
         */
        highlight:
        {
            required: false,
            type: Array,
            default: () => []
        },
    },

    data() {
        return {
            month: null,
            year: null,
            headline: '',
            today: null,
            calendar: {},
            mousedown: false,
            selection: [],
            withdrawn: [],
            selectAction: 'select',

            hovered: null,
            first: null, // = from
            last: null, // = until

            altKey: false,
            initializing: true,
        }
    },

    created()
    {
        this.init();

        events.$listen('calendar.set', dates =>
        {
            this.selection = dates;
        });

        events.$listen('calendar.reset', () =>
        {
            this.resetSelection();
        });

        events.$listen('calendar.add', day =>
        {
            this.add(day);
        });

        events.$listen('calendar.remove', day =>
        {
            this.remove(day);
        });

        events.$listen('calendar.prev', () =>
        {
            this.prevMonth();
        });

        events.$listen('calendar.next', () =>
        {
            this.nextMonth();
        });

        // set current year/month from date
        events.$listen('calendar.date', date =>
        {
            this.gotoDate(date);
        });

        if (this.selected.length)
        {
            this.selection = this.selected;

            if (this.mode === 'range')
            {
                if (this.range === 'week')
                {
                    this.first = this.selected[0];
                    this.last = moment(this.selected[0], 'YYYY-MM-DD').add(6, 'days').format('YYYY-MM-DD')
                    this.markRange();
                }
            }
        }

        window.addEventListener('keydown', this.keyHandler, false);
        window.addEventListener('keyup', this.keyHandler, false);
    },

    beforeDestroy()
    {
        events.$off('calendar.set');
        events.$off('calendar.reset');
        events.$off('calendar.add');
        events.$off('calendar.remove');

        window.removeEventListener('keydown', this.keyHandler, false);
        window.removeEventListener('keyup', this.keyHandler, false);
    },

    methods:
    {
        init()
        {
            // set today
            this.today = moment().format('YYYY-MM-DD');

            // calendar has pre-selected dates
            if (this.selected.length > 0 && (this.mode === 'single' || this.mode === 'range') && this.mode != 'readonly')
            {
                // jump to month/year of first day in selection
                this.first = this.selected[0];

                this.gotoDate(this.first);
            }
            else if (this.date)
            {
                this.gotoDate(this.date);
            }
            else
            {
                this.gotoToday();
            }

            this.initializing = false;
        },

        gotoToday()
        {
            this.gotoDate(this.today);
        },

        gotoDate(date)
        {
            this.month = moment(date).format('MM') * 1;
            this.year = moment(date).format('YYYY');

            this.fetchCalendar();
        },

        /*
         *  Sort days by date
         *
         *  This is not automatically done by the browser, since Object.keys()
         *  is not consistent across browsers!
         */
        sortDays(days)
        {
            return _.sortBy(days, 'date');
        },

        fetchCalendar()
        {
            let calendar = {};
            let week = {};

            let first = moment(this.year + '-' + ("00" + this.month).slice(-2) + '-01', "YYYY-MM-DD");
            let last = first.clone().endOf('month');

            if (first.isoWeekday() != 1) // 1 = MONDAY
            {
                let before = first.clone();

                do
                {
                    before.subtract(1, 'days');

                    var date = before.format('YYYY-MM-DD');

                    week[date] = {
                        day: before.format('D'),
                        date: date,
                        off: true,
                        today: this.today == date,
                        localized: before.format('LL'),
                    };
                }
                while (before.isoWeekday() != 1);

                _.sortBy(week, 'date');
            }

            for (let time = first.clone(); time <= last; time.add(1, 'days'))
            {
                var date = time.format('YYYY-MM-DD');

                week[date] = {
                    day: time.format('D'),
                    date: date,
                    off: false,
                    today: this.today == date,
                    localized: time.format('LL'),
                };

                if (time.isoWeekday() == 7) // 7 = SUNDAY
                {
                    calendar[time.isoWeek()] = week;
                    week = {};
                }
            }

            if (last.isoWeekday() != 7) // 7 = SUNDAY
            {
                let after = last.clone();

                do
                {
                    after.add(1, 'days');

                    var date = after.format('YYYY-MM-DD');

                    week[date] = {
                        day: after.format('D'),
                        date: date,
                        off: true,
                        today: this.today == date,
                        localized: after.format('LL'),
                    };
                }
                while (after.isoWeekday() != 7);

                calendar[after.isoWeek()] = week;
            }

            this.headline = first.format('MMMM') + ' ' + this.year;
            this.calendar = calendar;

            if ( ! this.initializing)
            {
                events.$emit('calendar.changed', {
                    month: this.month,
                    year: this.year
                });
            }
        },

        prevMonth()
        {
            this.month = this.month - 1;

            if (this.month == 0)
            {
                this.month = 12;
                this.year = parseInt(this.year) - 1;
            }

            this.fetchCalendar();
        },

        nextMonth()
        {
            this.month = this.month + 1;

            if (this.month == 13)
            {
                this.month = 1;
                this.year = parseInt(this.year) + 1;
            }

            this.fetchCalendar();
        },

        getClasses(day)
        {
            let classes = [];

            if (day.date < this.today)
            {
                // classes.push('is-inactive');
            }

            if (day.date > this.first && day.date <= this.hovered && ! this.last && ! this.altKey)
            {
                classes.push('is-period');
            }

            if (this.isSelected(day))
            {
                if (this.circled) 
                {
                    classes.push('is-circled');
                }
                else
                {
                    classes.push('is-selected');
                }
            }

            if (this.isHighlighted(day))
            {
                classes.push('is-highlighted');
            }

            if (day.date == this.today)
            {
                classes.push('is-today');
            }

            if (this.isBlocked(day))
            {
                classes.push('is-blocked');
            }

            if (day.off) classes.push('is-off');

            return classes;
        },

        isBlocked(day)
        {
            return this.blocked.indexOf(day.date) != -1;
        },

        isChooseable(day)
        {
            // cannot choose in the past
            return day.date >= this.today;
        },

        isSelected(day)
        {
            return this.selection.indexOf(day.date) != -1;
        },

        isHighlighted(day)
        {
            return this.highlight.indexOf(day.date) != -1;
        },

        resetSelection()
        {
            this.selection = [];
        },

        select($event, day)
        {
            this.mousedown = true;

            if (this.mode === 'readonly')
            {
                // if readonly, just handle single click callback
                if (typeof this.callback == 'function') this.callback(day.date);
                return;
            }

            if (this.isBlocked(day)) return;

            let selected = this.isSelected(day);
            this.selectAction = selected ? 'deselect' : 'select';

            if (this.mode === 'single')
            {
                if (!this.first && !this.last)
                {
                    if (this.selection.length > 0) this.resetSelection();

                    this.first = day.date;
                    this.add(day);
                }
                else
                {
                    if (this.first && !this.last)
                    {
                        if (day.date <= this.first)
                        {
                            this.resetSelection();

                            if (day.date === this.first) return this.first = null;

                            this.add(day);
                            this.first = day.date;
                        }
                        else
                        {
                            this.markRange(day);
                        }
                    }
                }
            }
            else if (this.mode === 'range')
            {
                if (this.range === 'day')
                {
                    this.resetSelection();
                    this.add(day);
                    this.first = day.date;
                }

                if (this.range === 'week')
                {
                    this.resetSelection();
                    this.first = moment(day.date, 'YYYY-MM-DD').startOf('week').format('YYYY-MM-DD');
                    this.last = moment(day.date, 'YYYY-MM-DD').endOf('week').format('YYYY-MM-DD');
                    this.markRange();
                }

                if (this.range === 'month')
                {
                    this.resetSelection();
                    this.first = moment(day.date, 'YYYY-MM-DD').startOf('month').format('YYYY-MM-DD');
                    this.last = moment(day.date, 'YYYY-MM-DD').endOf('month').format('YYYY-MM-DD');
                    this.markRange();
                }
            }
            else
            {
                if (selected && !this.first)
                {
                    this.remove(day);
                    return;
                }

                if (this.first && this.last)
                {
                    this.first = null;
                    this.last = null;
                }

                if ($event.altKey)
                {
                    selected ? this.remove(day) : this.add(day);

                    this.first = null;
                    this.last = null;

                    return;
                }

                if (!this.first && !this.last)
                {
                    this.first = day.date;
                    this.add(day);
                }
                else if (this.first && !this.last)
                {
                    if (day.date < this.first)
                    {
                        // remove first date
                        this.remove({date: this.first});

                        // replace first date
                        this.first = day.date;
                        this.add(day);
                        return;
                    }

                    this.markRange(day);
                }
            }

            this.handleCallback();
        },

        markRange(endDay)
        {
            if (typeof endDay !== 'undefined')
                this.last = endDay.date;

            var startDate = moment(this.first);
            var endDate = moment(this.last);

            while (startDate.isBefore(endDate) || startDate.isSame(endDate))
            {
                this.add({date: startDate.format('YYYY-MM-DD')});
                startDate.add(1, 'days');
            }

            this.first = null;
            this.last = null;
        },

        dragSelect(day)
        {
            if (this.mode === 'range' || this.mode === 'readonly') return;

            this.hovered = day.date;

            if ( ! this.mousedown || this.mode !== 'multi') return;

            // if (this.period && ! this.last) return;

            if (this.isBlocked(day)) return;

            if (this.selectAction == 'select')
            {
                this.add(day);

                this.last = day.date;
            }
            else
            {
                this.remove(day);
            }

            this.handleCallback();
        },

        add(day)
        {
            if (this.selection.indexOf(day.date) == -1)
            {
                this.selection.push(day.date);
                this.selection.sort();
            }
        },

        remove(day)
        {
            let key = this.selection.indexOf(day.date);

            if (key != -1) this.selection.splice(key, 1);
        },

        doneSelecting()
        {
            this.mousedown = false;
            this.hovered = null;
        },

        handleCallback()
        {
            if (typeof this.callback == 'function') this.callback(this.selection);
        },

        close()
        {
            events.$emit('toolbox.close');
        },

        keyHandler(e)
        {
            if (e.type == 'keydown' && e.keyCode == 27 && this.first && ! this.last)
            {
                // remove first date
                this.remove({ date: this.first });

                this.first = null;
            }

            if (e.keyCode == 18)
            {
                this.altKey = (e.type == 'keydown') ? true : false;
            }
        },
    },

    watch:
    {
        selected:
        {
            handler(val)
            {
                this.selection = val;
                this.first = val[0];
                
                if (this.mode != 'readonly') this.gotoDate(this.first);
            }
        }
    }
}
</script>



// WEBPACK FOOTER //
// Dates.vue?450d6d75